import {Injectable} from '@angular/core';
import {isAfter, subtractTime, nowDate, formatIso} from '../utilities/date-util';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {KcProgram, KcProgramLevel, KcStatus, Pledge, PledgeStatusType, PledgeType, User, UserType} from './api.client';
import {SecurityService} from './SecurityService';
import {Disposable} from '../utilities/Disposable';

@Injectable()
export class SystemService extends Disposable {

    kcStatus$: Observable<KcStatus[]>;
    kcStatus: KcStatus[];
    kcPrograms: KcProgram[];
    kcPrograms$: Observable<KcProgram[]>;
    currentUser: User;
    currentUser$: Observable<User>;
    pledgeTypes: PledgeType[] = [];
    pledgeTypes$: Observable<PledgeType[]>;


    get kcStatusDelete$(): any {
        return this.kcStatus$.exists()
            .pipe(
                map(status =>
                    status.find(kcStatus => kcStatus.isDeletedStatus)));
    }

    get kcStatusNew$(): Observable<KcStatus> {
        return this.kcStatus$.exists().pipe(
            map(status =>
                status.find(kcStatus => kcStatus.isNewStatus)));
    }

    get kcStatusRejected$(): any {
        return this.kcStatus$.exists().pipe(
            map(status =>
                status.filter(kcStatus => kcStatus.isRejectedStatus)));
    }

    get kcStatusApproved$(): any {
        return this.kcStatus$.exists().pipe(
            map(status =>
                status.find(kcStatus => kcStatus.statusName === 'Approval Recorded')));
    }

    get kcStatusCancel$(): any {
        return this.kcStatus$.exists().pipe(
            map(status =>
                status.find(kcStatus => kcStatus.isCancelledStatus)));
    }

    get kcStatusPendingApproval(): KcStatus {
        return this.kcStatus.find(status => status.statusName === 'Approval Pending');
    }

    get kcStatusNew(): KcStatus {
        return this.kcStatus.find(status => status.isNewStatus);
    }

    public getKcStatusById$(id: string): Observable<KcStatus> {
        return this.kcStatus$.exists().pipe(
            map(status =>
                status.find(kcStatus => kcStatus.id === id)));
    }

    kcStatusIsIncomplete(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return status.isNewStatus || status.isPendingApprovalStatus;
    }

    isKcStatusLocked(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return status.isSystemSet || !this.kcStatusIsIncomplete(status);
    }

    getKcStatusNameById(statusId: string): string {
        if (!this.kcStatus) {
            return '';
        }
        return this.kcStatus.find((status) => status.id === statusId).statusName;
    }

    get isSystemAdmin(): boolean {
        return this.currentUser.userType === UserType.SystemAdmin;
    }

    get isAgencyAdmin(): boolean {
        return this.currentUser.userType === UserType.AgencyAdmin;
    }

    get isRegionAdmin(): boolean {
        return this.currentUser.userType === UserType.RegionAdmin;
    }

    get isFundAdmin(): boolean {
        return this.currentUser.userType === UserType.FundAdmin;
    }

    get isSystemOrFundAdmin(): boolean {
        return this.isSystemAdmin || this.isFundAdmin;
    }

    get isViewer(): boolean {
        return this.currentUser.userType === UserType.Viewer;
    }

    get isUser(): boolean {
        return this.currentUser.userType === UserType.User;
    }

    get hasUwdaFundType(): boolean {
        return this.pledgeTypes.some(p => p.isUwda);
    }

    // Single source of truth for UWDA Pledges access, shared by the sidebar link
    // (SidebarComponent.showUwdaMenu) and the route guard (CanAccessUwdaPledgesGuard)
    // so nav visibility and route access can never diverge.
    canAccessUwdaPledges(): boolean {
        if (this.isSystemAdmin) {
            return true;
        }
        return (this.isAgencyAdmin || this.isRegionAdmin) && this.hasUwdaFundType;
    }

    // Single source of truth for "may the current user edit this user record", shared by
    // the CanEditUserGuard route guard and the user list (which uses it to decide whether
    // a row is a clickable link). Mirrors the existing guard rules exactly:
    //   - SystemAdmin may edit anyone
    //   - anyone may edit their own record
    //   - Users, Viewers and FundAdmins may not edit other users
    //   - AgencyAdmin / RegionAdmin may only edit Users and Viewers within their own agency
    canEditUser(target: User): boolean {
        if (!target) {
            return false;
        }
        if (this.isSystemAdmin) {
            return true;
        }
        if (this.currentUser.id === target.id) {
            return true;
        }
        if (this.isUser || this.isViewer || this.isFundAdmin) {
            return false;
        }
        return (target.userType === UserType.User || target.userType === UserType.Viewer)
            && target.agencyId === this.currentUser.agencyId;
    }

    // 003-retire-ngrx (US2): store-free. The security streams come from
    // SecurityService (US1); the old programDetail passthrough of the kcApp
    // slice had no readers and was deleted (consumers use
    // KcApplicationService.programDetail$ directly).
    constructor(private _securityService: SecurityService) {
        super();
        this.currentUser$ = this._securityService.currentUser$;
        this.pledgeTypes$ = this._securityService.pledgeTypes$;
        this.kcStatus$ = this._securityService.kcStatuses$;
        this.kcPrograms$ = this._securityService.kcPrograms$;

        this.currentUser$.subscribe(user => this.currentUser = user);
        this.pledgeTypes$.subscribe(pledgeTypes => this.pledgeTypes = pledgeTypes || []);
        this.kcStatus$.subscribe(status => this.kcStatus = status);
        this.kcPrograms$.subscribe(programs => this.kcPrograms = programs);
    }

    canCreatePledge(): boolean {
        return this.currentUser.userType !== UserType.Viewer;
    }

    loggedInUserCanApprove(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return this.isSystemOrFundAdmin && !status.isApprovedStatus && !this.isKcStatusLocked(status);
    }

    loggedInUserCanCancel(status: KcStatus): boolean {
        if (!status) {
            return false;
        }

        return this.isSystemOrFundAdmin &&
            !this.kcStatusIsIncomplete(status) &&
            !status.isCancelledStatus &&
            !status.isDeletedStatus &&
            !status.isRejectedStatus;
    }

    loggedInUserCanContinueEnrollment(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return !status.isPendingApprovalStatus && this.kcStatusIsIncomplete(status);
    }

    // Determine whether the logged in user can save this keeping current application id
    loggedInUserCanSave(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return (!this.isKcStatusLocked(status) &&
                !this.isViewer) ||
            this.isSystemAdmin;
    }

    loggedInUserCanReject(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return this.isSystemOrFundAdmin && status.isPendingApprovalStatus;
    }

    loggedInUserCanDelete(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return this.kcStatusIsIncomplete(status) && this.loggedInUserCanSave(status);
    }

    loggedInUserHasAgreed(): boolean {
        const user = this.currentUser;
        if (!user) {
            return false;
        }
        return user.uwAgreementDate && isAfter(user.uwAgreementDate, subtractTime(nowDate(), 1, 'year'));
    }

    showSaveApplicationBtn(status: KcStatus): boolean {
        if (!status) {
            return false;
        }
        return status.isPendingApprovalStatus && this.loggedInUserCanSave(status);
    }

    canAddNote(): boolean {
        return !this.isViewer;
    }

    canDeleteNote(): boolean {
        return !this.isViewer;
    }

    getQueryParameters(model?: any): string {
        model = model || {};
        if (model.startDate) {
            model.startDate = formatIso(model.startDate);
        }
        if (model.endDate) {
            model.endDate = formatIso(model.endDate);
        }
        const params = Object.keys(model)
            .filter((key) => !!model[key])
            .map((key) => `${key}=${model[key]}`)
            .join('&');
        return new URLSearchParams(params).toString();
    }

    getPledgeStatus(pledgeStatus: | PledgeStatusType | string): PledgeStatusType | null {
        if (pledgeStatus === 'unknown') {
            return PledgeStatusType.Unknown;
        } else if (pledgeStatus === 'new') {
            return PledgeStatusType.New;
        } else if (pledgeStatus === 'confirmed') {
            return PledgeStatusType.Confirmed;
        } else if (pledgeStatus === 'pending') {
            return PledgeStatusType.Pending;
        } else if (pledgeStatus === 'paid') {
            return PledgeStatusType.Paid;
        } else if (pledgeStatus === 'deleted') {
            return PledgeStatusType.Deleted;
        } else if (pledgeStatus === 'pendingManual') {
            return PledgeStatusType.PendingManual;
        } else {
            return null;
        }
    }
}
